import type { KeyboardEvent } from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '@wundo/ui';
import { PracticeCard, type PracticeCardProps } from './PracticeCard.tsx';
import { m } from '@/paraglide/messages.js';

export interface SortablePracticeCardProps extends Omit<
  PracticeCardProps,
  'handle' | 'ref' | 'dragTransform' | 'dragTransition' | 'dragging'
> {
  /* One place up or down the library, from the keyboard. */
  onMove: (direction: -1 | 1) => void;
}

/**
 * A library card in the list its admins arrange. Only the grip drags, so the
 * card still opens, edits and starts on a plain click. The arrows on the grip
 * move one place: dnd-kit's keyboard sensor scrolls the page on a long list.
 */
export function SortablePracticeCard({ onMove, ...props }: SortablePracticeCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.card.id });

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    /* Or the page scrolls away under the card that was just moved. */
    event.preventDefault();
    onMove(event.key === 'ArrowUp' ? -1 : 1);
  }

  return (
    <PracticeCard
      {...props}
      ref={setNodeRef}
      dragTransform={CSS.Translate.toString(transform) ?? undefined}
      dragTransition={transition}
      dragging={isDragging}
      handle={
        <IconButton
          variant='quiet'
          shape='square'
          /* Or a finger on the grip scrolls the page instead of dragging. */
          className='cursor-grab touch-none'
          aria-label={m.practice_move({ title: props.card.title })}
          {...attributes}
          {...listeners}
          /* After the library's own, so the arrows are ours to answer. */
          onKeyDown={handleKeyDown}
        >
          <GripVertical className='size-4' aria-hidden='true' />
        </IconButton>
      }
    />
  );
}
